import React, { useState, useEffect } from 'react';
import CustomSelect from './CustomSelect';
import api from '../../../api/axios';

const PaginatedSelect = ({
  endpoint,
  valueKey = 'id',
  labelKey = 'name',
  value,
  onChange,
  placeholder = "Select...",
  isSearchable = true,
  isDisabled = false,
  isClearable = false,
  isMulti = false,
  name,
  required = false,
  backgroundColor = '#f8fafc',
  formatOptionLabel,
}) => {
  const [options, setOptions] = useState([]);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state for server-side paginated endpoints
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [isServerPaginated, setIsServerPaginated] = useState(false);

  // Pagination state for local-sliced unpaginated endpoints
  const [localPage, setLocalPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    if (!endpoint) return;
    loadData(1);
  }, [endpoint]);

  const loadData = async (pageToFetch = 1) => {
    try {
      setLoading(true);
      const params = { page: pageToFetch, per_page: 20 };
      // skipLoader prevents the global full-screen overlay when fetching pages
      const response = await api.get(endpoint, { params, skipLoader: true });
      
      const rawData = response.data?.data;
      
      // Check if it's server paginated (has data array and current_page)
      if (rawData && typeof rawData === 'object' && Array.isArray(rawData.data) && rawData.current_page !== undefined) {
        setIsServerPaginated(true);
        const newItems = rawData.data.map(item => ({
          value: item[valueKey],
          label: item[labelKey] || item.name,
          original: item
        }));
        
        setPage(rawData.current_page);
        setLastPage(rawData.last_page);
        
        if (pageToFetch === 1) {
          setOptions(newItems);
          setVisibleOptions(newItems);
        } else {
          setOptions(prev => [...prev, ...newItems]);
          setVisibleOptions(prev => [...prev, ...newItems]);
        }
      } else {
        // Unpaginated backend (flat array)
        setIsServerPaginated(false);
        const flatArray = Array.isArray(rawData) ? rawData : (rawData?.data || []);
        const newItems = flatArray.map(item => ({
          value: item[valueKey],
          label: item[labelKey] || item.name,
          original: item
        }));
        
        setOptions(newItems);
        // Slicing for local pagination (load first 20 items)
        setVisibleOptions(newItems.slice(0, ITEMS_PER_PAGE));
        setLocalPage(1);
      }
    } catch (error) {
      console.error(`Error fetching data from ${endpoint}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleScrollToBottom = () => {
    if (loading) return;

    if (isServerPaginated) {
      if (page < lastPage) {
        loadData(page + 1);
      }
    } else {
      // Local slicing logic for unpaginated endpoints
      const maxLocalPage = Math.ceil(options.length / ITEMS_PER_PAGE);
      if (localPage < maxLocalPage) {
        const nextPage = localPage + 1;
        const nextItems = options.slice(0, nextPage * ITEMS_PER_PAGE);
        setVisibleOptions(nextItems);
        setLocalPage(nextPage);
      }
    }
  };

  // Find the selected option object(s) based on `value`
  const selectedOption = isMulti
    ? visibleOptions.filter(opt => (Array.isArray(value) ? value : []).includes(opt.value))
    : visibleOptions.find(opt => opt.value === value) || null;

  // We need to inject the currently selected option into visibleOptions if it's missing,
  // otherwise react-select won't show its label when selected.
  // E.g. user edits an item with an ID that hasn't been loaded into visible options yet.
  // For unpaginated endpoints, it's definitely in `options` but maybe not `visibleOptions`.
  // For paginated endpoints, we might have to fetch it, but usually, it's either in the first page or it's a known issue.
  // Let's at least handle the unpaginated case seamlessly:
  let finalVisibleOptions = [...visibleOptions];
  if (!isMulti && value && !selectedOption && !isServerPaginated) {
    const missingOption = options.find(opt => opt.value === value);
    if (missingOption) {
      finalVisibleOptions.unshift(missingOption);
    }
  }

  // To properly render the selected value in CustomSelect
  const finalValue = isMulti
    ? finalVisibleOptions.filter(opt => (Array.isArray(value) ? value : []).includes(opt.value))
    : finalVisibleOptions.find(opt => opt.value === value) || null;

  return (
    <CustomSelect
      name={name}
      options={finalVisibleOptions}
      value={finalValue}
      onChange={(opt) => {
        if (isMulti) {
          onChange(opt ? opt.map(o => o.value) : []);
        } else {
          onChange(opt?.value || '');
        }
      }}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isLoading={loading}
      isClearable={isClearable}
      isMulti={isMulti}
      required={required}
      backgroundColor={backgroundColor}
      onMenuScrollToBottom={handleScrollToBottom}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default PaginatedSelect;
